import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";

import useAccountChildren from "../../../../../hooks/useAccountChildren";
import useCompetition, {
  useLeagueEntry,
} from "../../../../../hooks/useCompetition";
import useProfileForm, { ProfileValues } from "../../hooks/useProfileForm";
import { ApiProfile } from "../../../../../types";
import usePreparedCompetitors from "../../hooks/usePreparedCompetitors";
import { patchProfile, registerChild } from "../../../../../api";
import { useEntryAuthWall } from "../../hooks/useAuthWall";
import useLocation from "../../../../../hooks/useLocation";
import getCategoryFromPrevious from "../../../../../utils/getCategoryFromPrevious";
import getGenericErrorInfo from "../../../../../utils/getGenericErrorInfo";
import useErrorAlert from "../../../../../hooks/useErrorAlert";

const MIN_BIRTH_YEAR = new Date().getFullYear() - 120;
const MAX_BIRTH_YEAR = new Date().getFullYear();

type FullProfileValues = ProfileValues & {
  yearOfBirth: "" | number;
  gender: string;
};

export default function useCompetitorDetailScreen() {
  useEntryAuthWall();

  const params = useParams();
  const state = useLocation<{ isJudge: boolean }>().state;
  const isJudge = state?.isJudge;

  const isEditing = params.profileUUID !== undefined;
  const { competition, isValidating: compIsValidating } = useCompetition(
    params.competitionUUID
  );
  const { children: profiles } = useAccountChildren();
  const [profile, setProfile] = useState<ApiProfile | undefined>(undefined);

  const { entry: leagueEntry, isValidating: leagueEntryIsValidating } =
    useLeagueEntry({
      leagueUUID: competition?.league?.uuid,
      userUUID: profile?.uuid,
    });

  const isLeagueEntryLoading =
    !leagueEntry &&
    (competition || compIsValidating) &&
    leagueEntryIsValidating;

  const previousCategoryUUID = useMemo(
    () => leagueEntry && getCategoryFromPrevious(leagueEntry.entries),
    [leagueEntry]
  );

  const [preparedCompetitors, setPreparedCompetitors] = usePreparedCompetitors(
    params.competitionUUID
  );
  const profileChoices = useMemo(() => {
    const existingEntryUUIDs = competition?.childEntries.map(
      (x) => x.user.uuid
    );
    const alreadyChosenUUIDs = preparedCompetitors.map((x) => x.userUUID);
    return profiles?.filter(
      (x) =>
        !existingEntryUUIDs?.includes(x.uuid) &&
        !alreadyChosenUUIDs.includes(x.uuid)
    );
  }, [profiles, preparedCompetitors, competition]);

  useEffect(() => {
    if (params.profileUUID && preparedCompetitors) {
      setProfile(
        preparedCompetitors.find((x) => x.userUUID === params.profileUUID)
          ?.profile
      );
      setIsCreatingUser(false);
    }
  }, [params.profileUUID, preparedCompetitors]);

  const [isCreatingUser, setIsCreatingUser] = useState<boolean | undefined>(
    undefined
  );

  const navigate = useNavigate();

  useEffect(() => {
    if (profiles && profiles.length === 0 && isCreatingUser === undefined) {
      setIsCreatingUser(true);
    }
  }, [isCreatingUser, profiles]);

  const { schema: baseSchema, initialValues: baseInitialValues } =
    useProfileForm({ competition, profile, previousCategoryUUID });

  const schema = baseSchema.shape({
    yearOfBirth: Yup.number()
      .typeError("Please enter a year")
      .positive()
      .integer()
      .required("Required")
      .min(MIN_BIRTH_YEAR, `Must be at least ${MIN_BIRTH_YEAR}`)
      .max(MAX_BIRTH_YEAR, "Must not be in the future"),
    gender: Yup.string()
      .required("Required")
      .oneOf(["MALE", "FEMALE", "OTHER"]),
  });

  const initialValues = {
    ...baseInitialValues,
    yearOfBirth: profile?.yearOfBirth || ("" as const),
    gender: profile?.gender || "",
    competitorNumber: "",
  };

  const { setError } = useErrorAlert();

  const onSubmit = async (values: FullProfileValues) => {
    // Create or update the profile.
    let updatedProfile: ApiProfile | undefined = undefined;
    if (values.yearOfBirth === "") {
      // should not happen as Yup should cast for us
      throw new Error("missing number value");
    }
    try {
      if (isCreatingUser) {
        updatedProfile = await registerChild({
          ...values,
          yearOfBirth: values.yearOfBirth,
          profilePicture:
            values.profilePicture instanceof File
              ? values.profilePicture
              : undefined,
        });
      } else if (profile) {
        updatedProfile = await patchProfile({
          ...values,
          yearOfBirth: values.yearOfBirth,
          uuid: profile.uuid,
        });
      }
      if (isEditing && profile) {
        setPreparedCompetitors(
          preparedCompetitors.map((x) =>
            x.userUUID === profile.uuid
              ? { ...x }
              : {
                  userUUID: x.userUUID,
                  categoryUUID: values.category,
                  competitorNumber: values.competitorNumber,
                }
          )
        );
      } else if (updatedProfile) {
        setPreparedCompetitors([
          ...preparedCompetitors,
          {
            userUUID: updatedProfile.uuid,
            categoryUUID: values.category,
            competitorNumber: values.competitorNumber,
          },
        ]);
      }
      if (updatedProfile) {
        navigate("..", { state: { isJudge } });
      }
    } catch (error) {
      setError(getGenericErrorInfo(error));
    }
  };

  const canChangeCategory = previousCategoryUUID === undefined;

  const onRemove = () => {
    if (profile && isEditing) {
      setPreparedCompetitors([
        ...preparedCompetitors.filter((x) => x.userUUID !== profile.uuid),
      ]);
      navigate("..", { state: { isJudge } });
    }
  };

  return {
    competition,
    profile,
    profiles: profileChoices,
    isCreatingUser,
    setIsCreatingUser,
    schema,
    initialValues,
    setProfile,
    onSubmit,
    canChangeCategory,
    onRemove,
    isEditing,
    isLeagueEntryLoading,
  };
}
